import React, { ReactNode, useEffect, useRef, createContext } from 'react';
import { setPosthogCapture } from './usePostHogEvents';
import type { CookieYesChanges, CookieYesDetail } from './types';
import { VITE_ENV } from 'config';
import loggerService, { LoggerLevel } from '../log';
import { COOKIEYES_BANNER_LOAD, COOKIEYES_CONSENT_UPDATE } from '.';

export const CookieManagementContext = createContext(undefined);

export const CookieManagementProvider: React.FC<{ children: ReactNode; url: string }> = ({
  children,
  url,
}) => {
  const scriptRef = useRef<HTMLScriptElement | null>(null);

  useEffect(() => {
    if (scriptRef.current) {
      return;
    }
    const script = document.createElement('script');
    script.src = url;
    script.onerror = () => {
      loggerService.log(LoggerLevel.ERROR, 'Error loading cookieyes script');
    };
    document.head.appendChild(script);
    scriptRef.current = script;
  }, [url]);

  useEffect(() => {
    const onCookieYesChange = (event: Event) => {
      const { detail } = event as CustomEvent<CookieYesChanges>;
      // Layer 2 guard: even if the user accepts analytics cookies, only opt in
      // where analytics is enabled for the region (GDPR — US only).
      setPosthogCapture(detail.accepted.includes('analytics') && VITE_ENV.posthog.analyticsEnabled);
    };

    const onCookieYesLoad = (event: Event) => {
      const { detail } = event as CustomEvent<CookieYesDetail>;
      setPosthogCapture(detail.categories.analytics && VITE_ENV.posthog.analyticsEnabled);
    };

    document.addEventListener(COOKIEYES_BANNER_LOAD, onCookieYesLoad);
    document.addEventListener(COOKIEYES_CONSENT_UPDATE, onCookieYesChange);

    return () => {
      document.removeEventListener(COOKIEYES_BANNER_LOAD, onCookieYesLoad);
      document.removeEventListener(COOKIEYES_CONSENT_UPDATE, onCookieYesChange);
    };
  }, []);

  return (
    <CookieManagementContext.Provider value={undefined}>
      {children}
    </CookieManagementContext.Provider>
  );
};
